import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { gql } from 'graphql-request';

import { gqlClient } from '@/api';
import type { Locale } from '@sonasi/shared-types';

import type { LegalKind } from './apps';

/**
 * The studio's own terms and privacy policy.
 *
 * The text every app without an override of its own shows — which is to say the
 * terms people actually agree to. Settings › Legal is the only place they are
 * written; a fresh database has none until somebody does.
 */

export interface StudioLegalDocument {
  kind: LegalKind;
  locale: string;
  title: string;
  /** `YYYY-MM-DD`. */
  effectiveOn: string;
  /** Markdown, one heading per section. */
  body: string;
  updatedAt: string;
}

const STUDIO_LEGAL_DOCUMENTS = gql`
  query StudioLegalDocuments {
    studioLegalDocuments {
      kind
      locale
      title
      effectiveOn
      body
      updatedAt
    }
  }
`;

const UPDATE_STUDIO_LEGAL = gql`
  mutation UpdateStudioLegal(
    $kind: LegalKind!
    $locale: ContentLocale!
    $document: StudioLegalInput!
  ) {
    updateStudioLegal(kind: $kind, locale: $locale, document: $document) {
      kind
      locale
      title
      effectiveOn
      body
      updatedAt
    }
  }
`;

const APPS_USING_STUDIO_LEGAL = gql`
  query AppsUsingStudioLegal($kind: LegalKind!, $locale: ContentLocale!) {
    appsUsingStudioLegal(kind: $kind, locale: $locale) {
      id
      name
      slug
    }
  }
`;

export const useStudioLegal = () =>
  useQuery({
    queryKey: ['studioLegal'],
    queryFn: async () =>
      (await gqlClient.request<{ studioLegalDocuments: StudioLegalDocument[] }>(
        STUDIO_LEGAL_DOCUMENTS,
      )).studioLegalDocuments,
  });

/**
 * Which apps would show this document.
 *
 * Asked per kind and language, because that is the granularity the answer
 * actually has: an app with its own English terms and no Japanese ones shows
 * its own English, not the studio's.
 */
export const useAppsUsingStudioLegal = (kind: LegalKind, locale: Locale) =>
  useQuery({
    queryKey: ['appsUsingStudioLegal', kind, locale],
    queryFn: async () =>
      (await gqlClient.request<{ appsUsingStudioLegal: { id: string; name: string; slug: string }[] }>(
        APPS_USING_STUDIO_LEGAL,
        { kind, locale: locale.toUpperCase() },
      )).appsUsingStudioLegal,
  });

export const useSaveStudioLegal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      kind: LegalKind;
      locale: Locale;
      document: { title: string; effectiveOn: string; body: string };
    }) =>
      (await gqlClient.request<{ updateStudioLegal: StudioLegalDocument }>(UPDATE_STUDIO_LEGAL, {
        kind: input.kind,
        locale: input.locale.toUpperCase(),
        document: input.document,
      })).updateStudioLegal,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['studioLegal'] });
      /*
       * And the audience: writing the Japanese document is exactly what stops
       * every app falling back to the English one, so the list under the
       * editor is wrong the moment the save lands.
       */
      void queryClient.invalidateQueries({ queryKey: ['appsUsingStudioLegal'] });
    },
  });
};

/** The stored document for one kind and language, or nothing written yet. */
export const documentFor = (
  documents: StudioLegalDocument[] | undefined,
  kind: LegalKind,
  locale: Locale,
): StudioLegalDocument | undefined =>
  documents?.find(
    (row) => row.kind === kind && row.locale.toLowerCase() === locale,
  );
